import React from 'react';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FormLabel from '@material-ui/core/FormLabel';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import WarningIcon from '@material-ui/icons/Warning';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import FileReader from '../FileReader/FileReader';
import TechnicalErrorAlert from '../../TechnicalErrorAlert/TechnicalErrorAlert';
import { userInfoState } from '../../HomePage/HomeSlice';

import useStyles from './styles';

export default function GlobalTicketDetails(props) {
  const { ticket, expanded, handleChange, statusError } = props;
  const [openTechnicalErrorAlert, setOpenTechnicalErrorAlert] =
    React.useState(false);
  const [technicalErrorMessage, setTechnicalErrorMessage] =
    React.useState(null);
  const classes = useStyles();
  const { t } = useTranslation();
  const user = useSelector(userInfoState);
  const dateFormatter = (date) => {
    return new Intl.DateTimeFormat('en-GB').format(new Date(date));
  };

  const handleCloseTechnicalErrorAlert = () => {
    setOpenTechnicalErrorAlert(false);
    setTechnicalErrorMessage(null);
  };

  return (
    <Accordion
      className={classes.firstAccordion}
      expanded={expanded}
      onChange={handleChange}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
      >
        <Typography className={classes.heading}>
          {t('Global maintenance ticket details')}
        </Typography>
        <Typography className={classes.secondaryHeading}>
          {t('Ticket and wagon main details')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails style={{ display: 'block' }}>
        {user.internal || user.hasEmilyDRights || user.externalMax ? (
          <div
            aria-label="internal user global ticket details"
            className={classes.container}
          >
            <Grid container spacing={1} className={classes.gridMargin}>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Ticket Number')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Ticket creation date')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Ticket Type')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Ticket status')}
                </FormLabel>
              </Grid>

              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {ticket.ticketNumber}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {dateFormatter(ticket.ticketCreationDate)}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {t(ticket.ticketType)}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                {statusError !== null ? (
                  <TextField
                    disabled
                    label=""
                    value={t(ticket.ticketStatus)}
                    variant="outlined"
                    helperText={statusError}
                    className={classes.ticketIncomplete}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <WarningIcon className={classes.warningIcon} />
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : (
                  <FormLabel className={classes.ticketDetail}>
                    {t(ticket.ticketStatus)}
                  </FormLabel>
                )}
              </Grid>
            </Grid>
            <Grid container spacing={1} className={classes.gridMargin}>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Wagon number')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Keeper')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Next scheduled revision date')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Assigned workshop')}
                </FormLabel>
              </Grid>

              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {ticket.wagonNumber}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {ticket.keeper}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {ticket.nextRevisionDate !== '-'
                    ? dateFormatter(ticket.nextRevisionDate)
                    : ticket.nextRevisionDate}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {ticket.assignedWorkshop}
                </FormLabel>
              </Grid>
            </Grid>
            <Grid container spacing={1} className={classes.gridMargin}>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Product type')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Business unit')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Site concerned')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Actual workshop')}
                </FormLabel>
              </Grid>

              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {ticket.productType}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {ticket.businessUnit}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {t(ticket.siteConcerned)}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {t(ticket.actualWorkshop)}
                </FormLabel>
              </Grid>
            </Grid>
            <Grid container spacing={1} className={classes.gridMargin}>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Loaded status')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('(Last) Cargo')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Wagon status')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Priority')}
                </FormLabel>
              </Grid>

              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {t(ticket.loadedStatus)}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {ticket.lastCargo}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {t(ticket.wagonStatus)}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {ticket.priority}
                </FormLabel>
              </Grid>
            </Grid>
            {ticket.attachments && ticket.attachments.length > 0 && (
              <FileReader
                attachments={ticket.attachments}
                anomalyIndex="0"
                setOpenTechnicalErrorAlert={setOpenTechnicalErrorAlert}
                setTechnicalErrorMessage={setTechnicalErrorMessage}
              />
            )}
          </div>
        ) : (
          <div
            aria-label="external user global ticket details"
            className={classes.container}
          >
            <Grid container spacing={1} className={classes.gridMargin}>
              <Grid item xs={4}>
                <FormLabel className={classes.formLabel}>
                  {t('Ticket Number')}
                </FormLabel>
              </Grid>
              <Grid item xs={4}>
                <FormLabel className={classes.formLabel}>
                  {t('Ticket creation date')}
                </FormLabel>
              </Grid>
              <Grid item xs={4}>
                <FormLabel className={classes.formLabel}>
                  {t('Wagon number')}
                </FormLabel>
              </Grid>
              <Grid item xs={4}>
                <FormLabel className={classes.ticketDetail}>
                  {ticket.ticketNumber}
                </FormLabel>
              </Grid>
              <Grid item xs={4}>
                <FormLabel className={classes.ticketDetail}>
                  {dateFormatter(ticket.ticketCreationDate)}
                </FormLabel>
              </Grid>
              <Grid item xs={4}>
                <FormLabel className={classes.ticketDetail}>
                  {ticket.wagonNumber}
                </FormLabel>
              </Grid>
            </Grid>
            <Grid container spacing={1} className={classes.gridMargin}>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Loaded status')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Keeper')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('Product type')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.formLabel}>
                  {t('(Last) Cargo')}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {t(ticket.loadedStatus)}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {ticket.keeperName}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {ticket.productType}
                </FormLabel>
              </Grid>
              <Grid item xs={3}>
                <FormLabel className={classes.ticketDetail}>
                  {ticket.lastCargo}
                </FormLabel>
              </Grid>
            </Grid>
          </div>
        )}
      </AccordionDetails>
      <TechnicalErrorAlert
        openTechnicalErrorAlert={openTechnicalErrorAlert}
        technicalErrorMessage={technicalErrorMessage}
        handleCloseTechnicalErrorAlert={handleCloseTechnicalErrorAlert}
      />
    </Accordion>
  );
}
